import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { adminClient, formatAdminDate } from '@/api/adminClient';
import ActAsDialog from '@/components/ActAsDialog';
import MembersCard from '@/components/settings/MembersCard';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { useOrganisation } from '@/lib/OrganisationContext';
import DiscardChangesDialog from './DiscardChangesDialog';
import { useDirtyNavigationGuard } from './useDirtyNavigationGuard';
import {
  CONTRAST_MODES, DC_LEVELS, ORGANISATION_SIZES, SECTORS, TEXT_SIZES,
  profileForm, profilePayload,
} from './customerProfile';

const emptyPanel = () => ({ state: 'loading', rows: [], error: '' });
const CONTROL = 'mt-1 min-h-11 w-full rounded-lg border border-input bg-background px-3 py-2 text-foreground disabled:opacity-60';

export default function AdminCustomerDetail() {
  const { id = '' } = useParams();
  const location = useLocation();
  const { beginActAs, selectedOrg } = useOrganisation();
  const routeGeneration = useRef(0);
  const routeIdRef = useRef(id);
  const detailController = useRef(null);
  const panelControllers = useRef({});
  const activeMutation = useRef(null);
  const mutationSequence = useRef(0);
  const headingRef = useRef(null);

  const [customer, setCustomer] = useState(null);
  const [form, setForm] = useState(() => profileForm());
  const [detailState, setDetailState] = useState('loading');
  const [detailError, setDetailError] = useState('');
  const [profileError, setProfileError] = useState('');
  const [notice, setNotice] = useState('');
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState('');
  const [lifecycleDialog, setLifecycleDialog] = useState(null);
  const [lifecycleError, setLifecycleError] = useState('');
  const [audit, setAudit] = useState(emptyPanel);
  const [sso, setSso] = useState(emptyPanel);
  const [actAsOpen, setActAsOpen] = useState(false);
  const [actAsBusy, setActAsBusy] = useState(false);
  const [actAsError, setActAsError] = useState('');

  routeIdRef.current = id;
  const resetDraft = useCallback(() => {
    setForm(profileForm(customer || {}));
    setDirty(false);
    setProfileError('');
  }, [customer]);
  const navigationGuard = useDirtyNavigationGuard(dirty, resetDraft, actAsBusy);
  const isCurrent = useCallback((target, generation) => (
    routeIdRef.current === target && routeGeneration.current === generation
  ), []);

  const loadPanel = useCallback(async (name, target = id, generation = routeGeneration.current) => {
    const setPanel = name === 'audit' ? setAudit : setSso;
    panelControllers.current[name]?.abort();
    const controller = new AbortController();
    panelControllers.current[name] = controller;
    setPanel((previous) => ({ ...previous, state: 'loading', error: '' }));
    const path = name === 'audit'
      ? `/admin/audit?organisation_id=${encodeURIComponent(target)}&limit=25`
      : `/admin/customers/${encodeURIComponent(target)}/sso`;
    try {
      const rows = await adminClient(path, { signal: controller.signal });
      if (!controller.signal.aborted && isCurrent(target, generation)) {
        setPanel({ state: 'ready', rows: Array.isArray(rows) ? rows : [], error: '' });
      }
    } catch (error) {
      if (!controller.signal.aborted && isCurrent(target, generation)) {
        setPanel({ state: 'error', rows: [], error: error.message || `${name} could not be loaded.` });
      }
    }
  }, [id, isCurrent]);

  const loadDetail = useCallback(async () => {
    const target = id;
    const generation = ++routeGeneration.current;
    detailController.current?.abort();
    Object.values(panelControllers.current).forEach((controller) => controller.abort());
    const controller = new AbortController();
    detailController.current = controller;
    activeMutation.current = null;
    setBusy('');
    setCustomer(null);
    setForm(profileForm());
    setDirty(false);
    setDetailState('loading');
    setDetailError('');
    setProfileError('');
    setNotice(location.state?.created ? 'Customer created. Invite an owner below when you are ready.' : '');
    setLifecycleDialog(null);
    setLifecycleError('');
    setActAsOpen(false);
    setActAsError('');
    setAudit(emptyPanel());
    setSso(emptyPanel());
    try {
      const row = await adminClient(`/admin/customers/${encodeURIComponent(target)}`, { signal: controller.signal });
      if (controller.signal.aborted || !isCurrent(target, generation)) return;
      setCustomer(row);
      setForm(profileForm(row));
      setDetailState('ready');
      void loadPanel('audit', target, generation);
      void loadPanel('sso', target, generation);
    } catch (error) {
      if (!controller.signal.aborted && isCurrent(target, generation)) {
        setDetailState('error');
        setDetailError(error.message || 'Customer could not be loaded.');
      }
    }
  }, [id, isCurrent, loadPanel, location.state?.created]);

  useEffect(() => {
    void loadDetail();
    return () => {
      routeGeneration.current += 1;
      detailController.current?.abort();
      Object.values(panelControllers.current).forEach((controller) => controller.abort());
      activeMutation.current = null;
    };
  }, [loadDetail]);

  useEffect(() => {
    if (detailState === 'ready' && customer?.id === id) headingRef.current?.focus();
  }, [customer?.id, detailState, id]);

  async function runMutation(kind, request, onCommitted, setError) {
    if (activeMutation.current) return;
    const target = id;
    const generation = routeGeneration.current;
    const token = ++mutationSequence.current;
    activeMutation.current = token;
    setBusy(kind);
    setError('');
    try {
      const row = await request(target);
      if (!isCurrent(target, generation) || activeMutation.current !== token) return;
      onCommitted(row);
    } catch (error) {
      if (isCurrent(target, generation) && activeMutation.current === token) {
        setError(error.message || 'The action could not be completed.');
      }
    } finally {
      if (isCurrent(target, generation) && activeMutation.current === token) {
        activeMutation.current = null;
        setBusy('');
      }
    }
  }

  function updateForm(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
    setDirty(true);
    setProfileError('');
  }

  async function saveProfile(event) {
    event.preventDefault();
    if (!customer || customer.status !== 'active' || !form.name.trim()) return;
    const payload = profilePayload(form, customer.accessibility_defaults || {});
    await runMutation(
      'profile',
      (target) => adminClient(`/admin/customers/${encodeURIComponent(target)}`, { method: 'PATCH', body: payload }),
      (row) => {
        setCustomer(row);
        setForm(profileForm(row));
        setDirty(false);
        setNotice('Profile saved.');
      },
      setProfileError,
    );
  }

  async function applyLifecycle(reason) {
    if (!lifecycleDialog) return;
    const action = lifecycleDialog.action;
    const success = lifecycleDialog.success;
    await runMutation(
      `lifecycle:${action}`,
      (target) => adminClient(`/admin/customers/${encodeURIComponent(target)}/${action}`, { method: 'POST', body: { reason } }),
      (row) => {
        setCustomer(row);
        if (!dirty) setForm(profileForm(row));
        setLifecycleDialog(null);
        setLifecycleError('');
        setNotice(success);
        void loadPanel('audit', id, routeGeneration.current);
      },
      setLifecycleError,
    );
  }

  async function startActAs(reason) {
    if (activeMutation.current) return;
    const target = id;
    const routeIntent = window.location.pathname;
    const generation = routeGeneration.current;
    const token = ++mutationSequence.current;
    activeMutation.current = token;
    setActAsBusy(true);
    setActAsError('');
    try {
      await beginActAs(target, reason, {
        shouldApply: () => window.location.pathname === routeIntent,
      });
      if (!isCurrent(target, generation) || activeMutation.current !== token) return;
      setActAsOpen(false);
      setNotice(`Act-as session started for ${customer.name}.`);
    } catch (error) {
      if (isCurrent(target, generation) && activeMutation.current === token) {
        setActAsError(error.message || 'Could not start act-as session.');
      }
    } finally {
      if (isCurrent(target, generation) && activeMutation.current === token) {
        activeMutation.current = null;
        setActAsBusy(false);
      }
    }
  }

  if (detailState === 'loading') return <p role="status" className="rounded-lg border border-border bg-card p-5">Loading customer…</p>;
  if (detailState === 'error') {
    return (
      <div role="alert" className="space-y-3 rounded-lg border border-destructive/40 bg-card p-5">
        <p className="text-red-800">{detailError}</p>
        <button type="button" className="min-h-11 rounded-lg border border-border px-3" onClick={loadDetail}>Retry customer</button>
      </div>
    );
  }

  const readOnly = customer.status !== 'active';
  const actionBusy = !!busy || actAsBusy;
  return (
    <div className="min-w-0 space-y-6" data-testid="customer-detail">
      <Link className="inline-flex min-h-11 items-center underline" to="/admin/customers">← Customers</Link>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 ref={headingRef} tabIndex="-1" className="break-words text-2xl font-bold text-foreground">{customer.name}</h1>
          <p className="mt-1 break-all text-sm text-muted-foreground">Customer ID: {customer.id}</p>
          <p className="mt-2"><span className="rounded-full border border-border px-3 py-1 text-sm capitalize">Status: {customer.status}</span></p>
        </div>
        <div className="flex flex-wrap gap-2">
          {customer.status === 'active' && <>
            <button type="button" disabled={actionBusy} className="min-h-11 rounded-lg border border-border px-3 disabled:opacity-50" onClick={() => { setActAsError(''); setActAsOpen(true); }}>Act as customer</button>
            <button type="button" disabled={actionBusy} className="min-h-11 rounded-lg border border-border px-3 disabled:opacity-50" onClick={() => { setLifecycleError(''); setLifecycleDialog({ action: 'suspend', title: 'Suspend customer', success: 'Customer suspended.' }); }}>Suspend</button>
          </>}
          {customer.status === 'suspended' && <button type="button" disabled={actionBusy} className="min-h-11 rounded-lg border border-border px-3 disabled:opacity-50" onClick={() => { setLifecycleError(''); setLifecycleDialog({ action: 'reinstate', title: 'Reinstate customer', success: 'Customer reinstated.' }); }}>Reinstate</button>}
          {customer.status !== 'archived' && <button type="button" disabled={actionBusy} className="min-h-11 rounded-lg border border-destructive/50 px-3 text-red-800 disabled:opacity-50" onClick={() => { setLifecycleError(''); setLifecycleDialog({ action: 'archive', title: 'Archive customer', success: 'Customer archived.' }); }}>Archive</button>}
        </div>
      </header>

      <div aria-live="polite" aria-atomic="true" className="min-h-6 text-sm">{notice}</div>
      <StatusCard customer={customer} />

      <section className="rounded-xl border border-border bg-card p-4 sm:p-5" aria-labelledby="profile-heading">
        <h2 id="profile-heading" className="text-lg font-semibold">Profile</h2>
        <form onSubmit={saveProfile} className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Organisation name" htmlFor="customer-name"><input id="customer-name" required disabled={readOnly || actionBusy} value={form.name} onChange={(event) => updateForm('name', event.target.value)} className={CONTROL} /></Field>
          <Field label="Sector" htmlFor="customer-sector"><select id="customer-sector" disabled={readOnly || actionBusy} value={form.sector} onChange={(event) => updateForm('sector', event.target.value)} className={CONTROL}><option value="">Not specified</option>{SECTORS.map((sector) => <option key={sector} value={sector}>{sector}</option>)}</select></Field>
          <Field label="Organisation size" htmlFor="customer-size"><select id="customer-size" disabled={readOnly || actionBusy} value={form.size} onChange={(event) => updateForm('size', event.target.value)} className={CONTROL}><option value="">Not specified</option>{ORGANISATION_SIZES.map((size) => <option key={size.value} value={size.value}>{size.label}</option>)}</select></Field>
          <Field label="Logo URL" htmlFor="customer-logo"><input id="customer-logo" type="url" disabled={readOnly || actionBusy} value={form.logo_url} onChange={(event) => updateForm('logo_url', event.target.value)} className={CONTROL} /></Field>
          <Field label="Disability Confident level" htmlFor="customer-level"><select id="customer-level" disabled={readOnly || actionBusy} value={form.current_dc_level} onChange={(event) => updateForm('current_dc_level', event.target.value)} className={CONTROL}>{DC_LEVELS.map((level) => <option key={level.value} value={level.value}>{level.label}</option>)}</select></Field>
          <Field label="Evidence retention (years)" htmlFor="customer-retention"><input id="customer-retention" type="number" min="0" step="1" required disabled={readOnly || actionBusy} value={form.evidence_retention_years} onChange={(event) => updateForm('evidence_retention_years', event.target.value)} className={CONTROL} /></Field>
          <fieldset className="rounded-lg border border-border p-4 sm:col-span-2">
            <legend className="px-1 text-sm font-semibold">Default accessibility settings</legend>
            <div className="mt-2 grid gap-4 sm:grid-cols-3">
              <Field label="Contrast" htmlFor="customer-contrast"><select id="customer-contrast" disabled={readOnly || actionBusy} value={form.contrastMode} onChange={(event) => updateForm('contrastMode', event.target.value)} className={CONTROL}>{CONTRAST_MODES.map((mode) => <option key={mode.value} value={mode.value}>{mode.label}</option>)}</select></Field>
              <Field label="Text size" htmlFor="customer-text-size"><select id="customer-text-size" disabled={readOnly || actionBusy} value={form.textSize} onChange={(event) => updateForm('textSize', event.target.value)} className={CONTROL}>{TEXT_SIZES.map((size) => <option key={size.value} value={size.value}>{size.label}</option>)}</select></Field>
              <label className="flex min-h-11 items-center gap-3 self-end rounded-lg border border-input bg-background px-3 py-2 text-sm font-medium text-foreground"><input type="checkbox" disabled={readOnly || actionBusy} checked={form.reduceMotion} onChange={(event) => updateForm('reduceMotion', event.target.checked)} />Reduce motion</label>
            </div>
          </fieldset>
          <div className="sm:col-span-2">
            {profileError && <p role="alert" className="mb-3 text-sm text-red-800">{profileError}</p>}
            <div className="flex flex-wrap gap-2">
              <button type="submit" disabled={readOnly || !dirty || actionBusy || !form.name.trim()} className="min-h-11 rounded-lg bg-teal-700 px-4 py-2 font-semibold text-white disabled:opacity-50">{busy === 'profile' ? 'Saving…' : 'Save profile'}</button>
              {dirty && <button type="button" disabled={actionBusy} onClick={resetDraft} className="min-h-11 rounded-lg border border-border px-4 py-2 disabled:opacity-50">Discard changes</button>}
            </div>
            {readOnly && <p className="mt-2 text-sm text-muted-foreground">Profile changes are unavailable while this customer is {customer.status}.</p>}
          </div>
        </form>
      </section>

      <MembersCard
        organisationId={id}
        organisationName={customer.name}
        canManage
        canManageOwnership
        providerTarget
        readOnly={readOnly}
        initialInviteOpen={!!location.state?.inviteOwner}
        initialInviteRole="owner"
        autoFocusInitialInvite={false}
      />
      <SsoPanel panel={sso} retry={() => loadPanel('sso')} />
      <AuditPanel panel={audit} retry={() => loadPanel('audit')} />

      <LifecycleDialog dialog={lifecycleDialog} busy={busy.startsWith('lifecycle:')} error={lifecycleError} onCancel={() => !busy && setLifecycleDialog(null)} onSubmit={applyLifecycle} />
      <ActAsDialog organisation={actAsOpen ? customer : null} onSubmit={startActAs} onClose={() => !actAsBusy && setActAsOpen(false)} busy={actAsBusy} error={actAsError} />
      <DiscardChangesDialog open={!!navigationGuard.pending} onCancel={navigationGuard.cancel} onConfirm={navigationGuard.confirm} />
      <p className="sr-only">Selected provider organisation remains {selectedOrg?.name || 'unchanged'}.</p>
    </div>
  );
}

function Field({ label, htmlFor, children }) {
  return <label htmlFor={htmlFor} className="block text-sm font-medium text-foreground">{label}{children}</label>;
}

function StatusCard({ customer }) {
  return <section className="rounded-xl border border-border bg-card p-4 sm:p-5" aria-labelledby="status-heading">
    <h2 id="status-heading" className="text-lg font-semibold">Customer status</h2>
    <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
      <div><dt className="text-muted-foreground">Reason</dt><dd className="mt-1 break-words font-medium">{customer.status_reason || 'No lifecycle reason recorded'}</dd></div>
      <div><dt className="text-muted-foreground">Changed</dt><dd className="mt-1 font-medium">{formatAdminDate(customer.status_changed_at)}</dd></div>
      <div><dt className="text-muted-foreground">Retention</dt><dd className="mt-1 font-medium">{customer.evidence_retention_years} years</dd></div>
      <div><dt className="text-muted-foreground">Scheduled purge</dt><dd className="mt-1 font-medium">{customer.purge_after || 'Not scheduled'}</dd></div>
    </dl>
    {customer.status === 'archived' && <p className="mt-4 rounded-lg border border-border bg-background p-3 text-sm text-foreground">This archived customer is retained read-only until its scheduled purge. Archive cannot be reversed.</p>}
  </section>;
}

function SsoPanel({ panel, retry }) {
  return <section className="rounded-xl border border-border bg-card p-4 sm:p-5" aria-labelledby="sso-heading">
    <h2 id="sso-heading" className="text-lg font-semibold">SSO connections</h2>
    {panel.state === 'loading' && <p role="status" className="mt-3">Loading SSO connections…</p>}
    {panel.state === 'error' && <PanelError message={panel.error} label="Retry SSO" retry={retry} />}
    {panel.state === 'ready' && (panel.rows.length === 0 ? <p className="mt-3 text-muted-foreground">No SSO connections configured.</p> : <ul className="mt-3 space-y-3" aria-label="SSO connections">{panel.rows.map((provider) => <li key={provider.id} className="rounded-lg border border-border p-3"><dl className="grid gap-2 text-sm sm:grid-cols-2"><div><dt className="text-muted-foreground">Provider</dt><dd className="break-words font-medium">{provider.providerId}</dd></div><div><dt className="text-muted-foreground">Domain</dt><dd className="break-all font-medium">{provider.domain || 'No domain configured'}</dd></div><div><dt className="text-muted-foreground">Domain verification</dt><dd className="font-medium">{provider.domainVerified ? 'Verified' : 'Not verified'}</dd></div><div><dt className="text-muted-foreground">Default role</dt><dd className="capitalize font-medium">{provider.defaultRole || 'Member'}</dd></div></dl></li>)}</ul>)}
  </section>;
}

function AuditPanel({ panel, retry }) {
  return <section className="rounded-xl border border-border bg-card p-4 sm:p-5" aria-labelledby="activity-heading">
    <h2 id="activity-heading" className="text-lg font-semibold">Recent activity</h2>
    {panel.state === 'loading' && <p role="status" className="mt-3">Loading recent activity…</p>}
    {panel.state === 'error' && <PanelError message={panel.error} label="Retry activity" retry={retry} />}
    {panel.state === 'ready' && (panel.rows.length === 0 ? <p className="mt-3 text-muted-foreground">No recent activity.</p> : <ul className="mt-3 space-y-2" aria-label="Recent customer activity">{panel.rows.map((row) => <li key={row.id} className="border-b border-border py-2 text-sm"><strong className="break-words">{row.action}</strong><span className="ml-2 text-muted-foreground">{formatAdminDate(row.created_at)}</span></li>)}</ul>)}
  </section>;
}

function PanelError({ message, label, retry }) {
  return <div className="mt-3 space-y-2"><p role="alert" className="text-sm text-red-800">{message}</p><button type="button" className="min-h-11 rounded-lg border border-border px-3" onClick={retry}>{label}</button></div>;
}

function LifecycleDialog({ dialog, busy, error, onCancel, onSubmit }) {
  const [reason, setReason] = useState('');
  useEffect(() => setReason(''), [dialog?.action]);
  const isArchive = dialog?.action === 'archive';
  return <Dialog open={!!dialog} onOpenChange={(open) => { if (!open && !busy) onCancel(); }}><DialogContent aria-describedby="lifecycle-description"><DialogHeader><DialogTitle>{dialog?.title}</DialogTitle><DialogDescription id="lifecycle-description">{isArchive ? 'Archive is irreversible. API keys and active support sessions will be revoked, and purge will be scheduled using the customer retention period.' : 'Give a clear reason for this lifecycle change. It will be retained in the audit history.'}</DialogDescription></DialogHeader><label htmlFor="lifecycle-reason" className="text-sm font-medium text-foreground">Reason<textarea id="lifecycle-reason" autoFocus minLength={10} maxLength={500} disabled={busy} value={reason} onChange={(event) => setReason(event.target.value)} aria-describedby="lifecycle-help lifecycle-error" className="mt-1 min-h-28 w-full rounded-lg border border-input bg-background p-3 text-foreground" /></label><p id="lifecycle-help" className="text-xs text-muted-foreground">10–500 characters.</p>{error && <p id="lifecycle-error" role="alert" className="text-sm text-red-800">{error}</p>}<DialogFooter><button type="button" disabled={busy} onClick={onCancel} className="min-h-11 rounded-lg border border-border px-4 py-2 disabled:opacity-50">Cancel</button><button type="button" disabled={busy || reason.trim().length < 10 || reason.trim().length > 500} onClick={() => onSubmit(reason.trim())} className="min-h-11 rounded-lg bg-teal-700 px-4 py-2 font-semibold text-white disabled:opacity-50">{busy ? 'Applying…' : dialog?.title}</button></DialogFooter></DialogContent></Dialog>;
}
